/**
 * Everything one `GetExerciseResponse` carries that a LIST row does not need.
 *
 * `exerciseListItem.ts` is the row: what a table draws. This is that row plus the six fields a
 * surface describing ONE movement reads — its prose, its steps, its trainer links, its demo film,
 * and the two facts that say whose it is. `ExerciseDetail extends ExerciseListItem`, so one pass
 * over the catalogue's response answers both questions and nothing is projected twice.
 *
 * ── Why a second module rather than six more fields on the row ───────────────────────────────
 * The row is drawn 477 times on one screen and the detail once. Keeping them apart is what lets a
 * table's projection stay a table's projection — and it is also what makes the `ui/` rule
 * enforceable: this file and `exerciseListItem.ts` are the only two that name the DTO, so a
 * component reaching for `links` has to come through here.
 *
 * ── `GET /exercise` already carries all of it ────────────────────────────────────────────────
 * There is no second read. The catalogue endpoint returns the full response per movement — prose,
 * instructions and the RESOLVED links (the library's own, or the trainer's overlay where they have
 * one) — which is why the header's `Z materiałami` figure can be counted over the whole catalogue
 * without asking the backend for a count it does not offer (owner decision D14, 17 September 2026).
 *
 * The one thing NOT here is `GET /exercise/{id}/overlay`, which answers a different question:
 * whether the trainer has overridden the library's links, and what with. `null` links there mean
 * "inherit" and `[]` means "override to none" — a distinction the resolved array below cannot
 * carry. `api/exerciseDetailQueries.ts` holds that read.
 */

import type {
  TrainWithMeModulesExercisesDtosResponsesExerciseLinkResponse,
  TrainWithMeModulesExercisesDtosResponsesGetExerciseResponse,
} from '~/shared/api/generated/model';

import { checkExerciseLink } from './exerciseLinks';
import { toExerciseListItem, type ExerciseListItem } from './exerciseListItem';

type ApiExercise = TrainWithMeModulesExercisesDtosResponsesGetExerciseResponse;
type ApiLink = TrainWithMeModulesExercisesDtosResponsesExerciseLinkResponse;

/**
 * One material a trainer has attached to a movement.
 *
 * `displayHost` is the backend's own answer to "what should this read as" — a punycode host
 * unwrapped where it is internationalised — and it is what the brand mark is chosen from and what
 * the row prints under the label. The raw `host` is kept because a policy check is made against the
 * ADDRESS, never against the pretty spelling of it.
 */
export interface ExerciseLink {
  readonly url: string;
  /** The trainer's own words for it. Empty where they gave none — the url then stands alone. */
  readonly label: string;
  /** The host as it should READ. `youtube.com`, `drive.google.com`, an unwrapped IDN. */
  readonly displayHost: string;
  /**
   * The host as it IS — punycode where the name is internationalised, so `xn--80ak6aa92e.com`
   * rather than the `аpple.com` the reader sees. Never the same question as `displayHost`, and the
   * two are only equal on an ASCII host.
   */
  readonly host: string;
  /**
   * The server's own homograph warning: this host is an IDN, so its pretty spelling may not be the
   * place it goes. `аpple.com` with a Cyrillic а is a different destination from `apple.com` and
   * reads identically, and `brandKeyOf(displayHost)` would award it a familiar mark.
   *
   * It is CARRIED and not yet drawn: what a warning looks like is a design question the owner has
   * not answered (deviations register, entry 17). What the projection must not do is throw the
   * server's answer away, which is what it did.
   */
  readonly hostIsInternationalized: boolean;
}

/** One movement, fully described. */
export interface ExerciseDetail extends ExerciseListItem {
  /** The trainer-facing prose, already resolved into the reader's language. Empty where absent. */
  readonly description: string;
  /** `Jak wykonać`, in order. Empty where the movement carries none. */
  readonly instructions: readonly string[];
  /** The RESOLVED materials: the library's own, or this trainer's overlay where they have one. */
  readonly links: readonly ExerciseLink[];
  /** Absolute url of the demo film, or `null`. */
  readonly videoUrl: string | null;
  /**
   * True when this is a LIBRARY movement the trainer has customised — an overlay on somebody
   * else's exercise, not one of their own. `isCustom` and this are the two halves of ownership and
   * they are read together; `model/exerciseOwnership.ts` is the one module that does the reading.
   */
  readonly isCustomized: boolean;
  /** The trainer who owns it, or `null` for a library movement. */
  readonly ownerTrainerId: number | null;
}

/**
 * Every material the wire sent, checked against the SAME policy the editor checks a typed one
 * against, and the refusals dropped.
 *
 * ── The read path has to ask, because the server only asks on WRITE ─────────────────────────
 * `ExternalLinkPolicy` says so in its own summary: it gates `PUT /exercise/{id}/overlay/links` and
 * `PATCH /exercise/{id}`, and it hands a stored link straight back as accepted. So a row that
 * reached the table before the policy existed, or by any route that did not go through it, arrives
 * here as an ordinary string and used to reach `MaterialsBlock`'s `href` untouched. The session
 * lives in `localStorage`, which makes a `javascript:` href in a trainer's own card the whole
 * attack surface for account takeover — `CLAUDE.md`'s own words.
 *
 * `checkExerciseLink` is the mirror this app already owns, and it is called rather than a second
 * predicate written beside it: two sanitisers are how one of them ends up out of step.
 *
 * ── The LABEL is not part of the question here, and that is deliberate ──────────────────────
 * The check is handed an empty label. On the write path the label's 80-character cap is a real
 * rule, because the trainer is composing something and the server will refuse it. On the read path
 * the question is only whether this ADDRESS may be put in an `href`, and refusing a stored link
 * because somebody's caption is a character too long would take a working video off a card for a
 * reason that has nothing to do with safety.
 *
 * A dropped link is dropped rather than drawn inert: the block already counts what it shows („n z
 * 3"), and a row that cannot be pressed is a control that lies about being one.
 *
 * A blank label stays a blank label rather than being filled with the url, because whether to fall
 * back is the ROW's decision and not the projection's.
 */
function toLinks(api: readonly ApiLink[]): ExerciseLink[] {
  return api.flatMap((link) => {
    const url = (link.url ?? '').trim();
    if (!checkExerciseLink({ url, label: '' }).ok) return [];
    return [
      {
        url,
        label: (link.label ?? '').trim(),
        // The raw host is the fallback, and an absent one leaves the empty string: the row prints
        // the host under the label, and `undefined` would render as nothing while claiming a line.
        displayHost: (link.displayHost ?? link.host ?? '').trim(),
        host: (link.host ?? '').trim(),
        hostIsInternationalized: link.hostIsInternationalized,
      },
    ];
  });
}

/** Project one wire item into the full description of a movement. */
export function toExerciseDetail(api: ApiExercise): ExerciseDetail {
  return {
    ...toExerciseListItem(api),
    description: (api.description ?? '').trim(),
    instructions: (api.instructions ?? []).filter((step) => step.trim().length > 0),
    links: toLinks(api.links ?? []),
    videoUrl: api.videoUrl ?? null,
    isCustomized: api.isCustomized ?? false,
    ownerTrainerId: api.personalTrainerId ?? null,
  };
}

/**
 * The whole response, fully described.
 *
 * Drops id-less rows for the reason `toExerciseListItems` does: a row keyed on zero collides with
 * every other such row, in React's reconciler and in any map built from this list.
 */
export function toExerciseDetails(
  items: readonly ApiExercise[] | undefined,
): ExerciseDetail[] {
  if (items === undefined) return [];
  return items.filter((item) => (item.id ?? 0) > 0).map(toExerciseDetail);
}
